import _ from "lodash";
import { type LucideIcon } from "lucide-react";
import {
  SortableDefinitionsType,
  SortableGroupingItemType,
  SortableType,
} from "@/components/@types";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { icons } from "@/objects/@icons";
import { useSortChipState } from "@/components/organisms/UtilityBar/FilterChip/FilterChipStateContext";
import { Button } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";

export function SortChipGroupingSection({
  index,
  sortable,
  sortables,
  ReactGroupingIcon,
  grouping,
  sortableDefinition,
  dropdownOpenIndexes,
  setDropdownOpenIndexes,
}: {
  index: number;
  sortable: SortableType;
  sortables: SortableType[];
  ReactGroupingIcon: LucideIcon;
  grouping: SortableGroupingItemType;
  sortableDefinition: SortableDefinitionsType;
  dropdownOpenIndexes: any[];
  setDropdownOpenIndexes: (value: any) => void;
}) {
  const { setSortables } = useSortChipState();

  return (
    <Popper
      isOpenState={[
        dropdownOpenIndexes[index]?.grouping,
        (isOpen) => {
          const newDropdownOpenIndexes = [...dropdownOpenIndexes];
          newDropdownOpenIndexes[index] = {
            ...newDropdownOpenIndexes[index],
            grouping: isOpen,
          };
          setDropdownOpenIndexes(newDropdownOpenIndexes);
        },
      ]}
      anchor={
        <Button
          variant="secondary"
          size="sm"
          data-testid={`sort-chip-grouping-${grouping?.name}`}
          className="w-60 justify-start overflow-hidden text-ellipsis"
        >
          {ReactGroupingIcon && (
            <ReactGroupingIcon
              style={{
                color:
                  "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
                minWidth: "16px",
                maxWidth: "16px",
                minHeight: "16px",
                maxHeight: "16px",
              }}
            />
          )}
          <Typography className="truncate overflow-hidden text-ellipsis">
            {grouping?.name}
          </Typography>
          <div className="grow" />
          <icons.chevronDown
            size="1em"
            style={{
              color:
                "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
              transform: "translateY(1px)",
            }}
          />
        </Button>
      }
      floatingStyle={{ padding: 0 }}
    >
      <List<SortableDefinitionsType["operators"][number]>
        data={_.flatten(sortableDefinition?.operators) || []}
        onItemClick={(groupingItem) => {
          setDropdownOpenIndexes((prev: any) => {
            const newDropdownOpenIndexes = [...prev];
            newDropdownOpenIndexes[index] = {
              ...prev[index],
              grouping: false,
            };
            return newDropdownOpenIndexes;
          });
          const newSortables = [...sortables];
          newSortables[index] = {
            ...sortable,
            value: groupingItem.key,
          };
          setSortables(newSortables);
        }}
      >
        <List.Items>
          {(groupingItem: any) => (
            <List.Item
              value={groupingItem}
              data-testid={`sort-grouping-option-${groupingItem.name}`}
            >
              <div className="min-w-100 flex gap-1 items-center flex-row flex-nowrap pl-1">
                <Icon ReactIcon={groupingItem.ReactIcon} size="sm" />
                <Typography>{groupingItem.name}</Typography>
              </div>
            </List.Item>
          )}
        </List.Items>
      </List>
    </Popper>
  );
}
